import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import GlimpseCard from "../../components/card/card.component";
import Switch from "components/switch/switch.component";
import FormikSelect from "components/select/formik-select";
import FormikInput from "components/input/formik-input";
import {
  displayError,
  displaySuccess,
  displayWarning,
} from "components/Toast/toast.component";
import { Form, Formik, FormikHelpers } from "formik";
import { IChangePassword, IModuleQuiz, IUser } from "interfaces/user.interface";
import { useEffect, useState } from "react";
import { changePassword } from "services/auth.service";
import * as Yup from "yup";

import { passwordRegex } from "constants/regex";
import { getPartnerData, subscribeToEmail, updateEducationInfo, updateModuleQuiz, updateUserData } from "services/setting.service";
import { setCredentials } from "store/slices/auth/auth-slice";
import { PRIMARY_OPTIONS, SECONDARY_OPTIONS } from "constants/education.constants";
import PartnerDataSetting from "./PartnerData.setting";
import EndUserData from "./userData.setting";
import { useDispatch } from "react-redux";
import Button from "components/button/button";

const initialValues = {
  old_password: "",
  new_password: "",
  confirm_password: "",
};

interface IEducationInfo {
  education_stage: string;
  extra_info: string;
  other_text?: string;
}

const SettingsScreen: React.FC = () => {
  const { userInfo } = useSelector((state: any) => state.auth);
  const [passwords, setPasswords] = useState<IChangePassword>(initialValues);
  const [moduleQuiz, setModuleQuiz] = useState<boolean>(false);
  const [educationInfo, setEducationInfo] = useState<IEducationInfo>({
    education_stage: "",
    extra_info: "",
    other_text: "",
  });

  const dispatch = useDispatch()

  const { data: endUserInfo, refetch } = useQuery({
    queryKey: ["getEndUserData"],
    queryFn: getPartnerData,
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: false,
  });

  const reasonsForPackagePurchase =
    userInfo?.user?.subscription?.subscription_obj?.reasons.split(",");

  useEffect(() => {
    if (endUserInfo && endUserInfo.role === "partner") {
      setModuleQuiz(endUserInfo.profile.model_quiz);
    }
  }, [endUserInfo]);

  useEffect(() => {
    if (userInfo && userInfo.role === "partner") {
      refetch();
    }
  }, [userInfo]);

  // Initialize education info from user data
  useEffect(() => {
  if (userInfo?.user?.profile) {
    const profile = userInfo.user.profile;
    const isExtraInfoOther =
      profile.extra_info &&
      profile.education_stage &&
      SECONDARY_OPTIONS[profile.education_stage] &&
      !SECONDARY_OPTIONS[profile.education_stage].some(
        (option: { value: string; label: string }) => option.value === profile.extra_info
      );
    setEducationInfo({
      education_stage: profile.education_stage || "",
      extra_info: isExtraInfoOther ? "other" : (profile.extra_info || ""),
      other_text: isExtraInfoOther ? profile.extra_info : "",
    });
  }
}, [userInfo]);

  // validationSchemas
  const passwordValidationSchema = Yup.object({
    old_password: Yup.string().required("Current password is required."),
    new_password: Yup.string()
      .matches(
        passwordRegex,
        "Password should be minimum 8 characters with at least 1 number and 1 letter."
      )
      .required("New password is required."),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password")], "Passwords must match")
      .required("Please confirm your new password."),
  });

  const educationValidationSchema = Yup.object({
    education_stage: Yup.string().required("Please select an option"),
    extra_info: Yup.string().required("Please select an option"),
    other_text: Yup.string().when("extra_info", {
      is: "other",
      then: (schema) => schema.required("Please specify"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  // handle password change
  const handleChangePassword = useMutation({
    mutationFn: (data: IChangePassword) => changePassword(data),
    retry: 0,
    onSuccess: () => {
      displaySuccess("Password changed successfully.");
      setPasswords(initialValues);
    },
    onError: () => {
      displayError("Password change failed");
    },
  });

  const handlePasswordSubmit = (
    values: IChangePassword,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    setSubmitting(false);
    if (values.new_password === values.confirm_password) {
      handleChangePassword.mutateAsync(values);
    } else {
      displayWarning("Password must be same");
    }
  };

  // handle module quiz
  const handlemoduleQuiz = useMutation({
    mutationFn: (data: IModuleQuiz) => updateModuleQuiz(data),
    retry: 0,
    onSuccess: (data) => {
      displaySuccess("Module quiz updated successfully.");
      setModuleQuiz(data.model_quiz);
    },
    onError: () => {
      displayError("Could not update module quiz ");
    },
  });

  const handleModuleQuizSubmit = (values: boolean) => {
    const payload = { model_quiz: values };
    handlemoduleQuiz.mutateAsync(payload);
  };

  // handle education info update
  const handleEducationUpdate = useMutation({
    mutationFn: (data: IEducationInfo) => updateEducationInfo(data),
    retry: 0,
    onSuccess: (data) => {
      displaySuccess("Education information updated successfully.");
      // Update local state and Redux store
      let existingCredentials = JSON.parse(JSON.stringify(userInfo));
      existingCredentials.user.profile.education_stage = data.education_stage;
      existingCredentials.user.profile.extra_info = data.extra_info;
      existingCredentials.user.profile.other_text = data.other_text;
      dispatch(setCredentials({ ...existingCredentials }));
    },
    onError: () => {
      displayError("Failed to update education information");
    },
  });

  const handleEducationSubmit = (
    values: IEducationInfo,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    setSubmitting(false);
    const payload: IEducationInfo = {
      education_stage: values.education_stage,
      extra_info: values.extra_info === "other" ? (values.other_text || "") : values.extra_info,
      other_text: values.other_text,
    };
    handleEducationUpdate.mutateAsync(payload);
  };

  const queryClient = useQueryClient()
  // Mutation to update subscription status in backend
  const emailSubscriptionMutation = useMutation({
    mutationFn: (payload: { email_subscribed: boolean }) => subscribeToEmail(payload),

    onSuccess: async (data, payload) => {
      let existingCredentials = JSON.parse(JSON.stringify(userInfo));
      existingCredentials.user.profile.email_subscribed = !existingCredentials.user.profile.email_subscribed;
      dispatch(setCredentials({ ...existingCredentials }));
    },
    onError: (err) => {
      displayError("Failed to update email subscription.");
    }
  });

  // Handle toggle change and mutate backend
  const handleSubscriptionChange = (checked: boolean) => {
    emailSubscriptionMutation.mutate({ email_subscribed: checked });
  };

  return (
    <div className="flex flex-col gap-6 max-w-[616px]">
      {userInfo.user.role !== "super" && (
        <>
          <div className="pl-3 text-body text-text-muted">
            View or update your account information.
          </div>
          <GlimpseCard
            title="General Info"
            titleClass="text-h2"
            className="border-b rounded-none shadow-none border-border"
          >
            {userInfo.user.role === "user" && <EndUserData />}
            {userInfo && userInfo.user.role === "partner" && (
              <PartnerDataSetting />
            )}
          </GlimpseCard>
        </>
      )}

      <GlimpseCard
        title="Password"
        titleClass="text-h2"
        className="border-b rounded-none shadow-none border-border"
      >
        <div className="pt-1 pb-3 text-body text-text-muted">
          To reset your password, provide your current password and choose a
          new password.
        </div>
        <div>
          <div className="flex text-start">
            <div className="w-[300px]">
                <Formik
                  initialValues={passwords}
                  validationSchema={passwordValidationSchema}
                  onSubmit={handlePasswordSubmit}
                >
                  {({ isSubmitting, setFieldValue, values, errors }) => (
                    <Form className="flex flex-col gap-4">
                      <FormikInput
                        type="password"
                        id="old_password"
                        label="Password"
                        as="input"
                        name="old_password"
                        placeholder="Password"
                        isPassword={true}
                        maxLength={50}
                      />
                      <FormikInput
                        type="password"
                        id="new_password"
                        label="New Password"
                        as="input"
                        name="new_password"
                        placeholder="Password"
                        isPassword={true}
                        maxLength={50}
                      />
                      <FormikInput
                        type="password"
                        id="confirm_password"
                        label="Confirm New Password"
                        as="input"
                        name="confirm_password"
                        placeholder="Password"
                        isPassword={true}
                        maxLength={50}
                      />

                      <div className="flex justify-between mt-2">
                        <Button
                          variant="primary"
                          type="submit"
                        >
                          Change Password
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </GlimpseCard>

      {userInfo.user.role === "partner" && (
        <GlimpseCard
          title="Module Quiz"
          titleClass="text-h2"
          className="border-b rounded-none shadow-none border-border"
        >
          <div className="pt-1 pb-3 text-body text-text-heading">
            Module quizzes are used at the end of career module to test
            members knowledge of the material covered.
            <span className="font-bold">
              {" "}This action is applied to all members associated with your
              account.
            </span>
          </div>
          <Switch
            checked={moduleQuiz}
            onChange={(checked) => {
              handleModuleQuizSubmit(checked);
            }}
          />
        </GlimpseCard>
      )}

      {userInfo.user.role !== "super" && (
        <>
          {userInfo.user.role === "user" &&
            userInfo.user.subscription.gift_by === null && (
              <GlimpseCard
                title="My Plan"
                titleClass="text-h2"
                className="border-b rounded-none shadow-none border-border"
              >
                <div className="pt-1 pb-3 text-body text-text-muted">
                  View your plan details below.
                </div>
                <div className="flex flex-col pb-4 text-md">
                    <div className="font-bold">
                      {userInfo.user.subscription.subscription_obj.type}
                    </div>
                    <span className="">
                      ${userInfo.user.subscription.subscription_obj.amount}
                    </span>
                  </div>
                  {reasonsForPackagePurchase && (
                    <ul className="pb-4">
                      {reasonsForPackagePurchase.map(
                        (item: string, index: number) => {
                          return (
                            <li
                              key={index}
                              className="flex items-center gap-2 "
                            >
                              <div>
                                <svg
                                  width="9"
                                  height="8"
                                  viewBox="0 0 9 8"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8.45101 1.68765L4.30963 7.30771C4.21086 7.43875 4.06358 7.52461 3.90089 7.546C3.73819 7.56739 3.57373 7.52251 3.44444 7.42145L0.487093 5.05702C0.226126 4.84818 0.183869 4.46733 0.392709 4.20636C0.60155 3.94539 0.982404 3.90314 1.24337 4.11198L3.70944 6.08496L7.47692 0.97191C7.60046 0.786525 7.81589 0.684029 8.03766 0.705117C8.25944 0.726205 8.45169 0.867464 8.53807 1.07281C8.62446 1.27816 8.59101 1.51436 8.45101 1.68765Z"
                                    fill="black"
                                  />
                                </svg>
                              </div>
                              <div> {item}</div>
                            </li>
                          );
                        }
                      )}
                    </ul>
                  )}
              </GlimpseCard>
            )}
          {userInfo.user.role === "partner" && (
            <GlimpseCard
              title="My Plan"
              titleClass="text-h2"
              className="border-b rounded-none shadow-none border-border"
            >
              <div className="pt-1 pb-3 text-body text-text-muted">
                Manage your plan details below.
              </div>
              <div className="pb-4 text-lg">
                <div className="text-body-sm text-text-muted">Membership Licenses</div>
                {userInfo.user.profile.total_licenses}
                <div className="flex pt-4">
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => window.location.href = 'mailto:blake@careersight.com'}
                  >
                    Add Licenses
                  </Button>
                </div>
              </div>
            </GlimpseCard>
          )}

          {/* Education Information Section - Added below My Plan */}
          {userInfo.user.role === "user" && (
              <GlimpseCard
                title="User Description"
                titleClass="text-h2"
                className="border-b rounded-none shadow-none border-border"
              >
                <div className="pt-2">
                  <Formik
                    initialValues={educationInfo}
                    validationSchema={educationValidationSchema}
                    enableReinitialize={true}
                    onSubmit={handleEducationSubmit}
                  >
                    {({ values, setFieldValue, isSubmitting }) => (
                      <Form>
                        <div className="space-y-4">
                          {/* Primary Dropdown */}
                          <div className="w-full">
                            <FormikSelect
                              name="education_stage"
                              options={PRIMARY_OPTIONS}
                              showLabel={true}
                              label="Which of these describes you best?"
                              id="education_stage"
                              placeholder="Select.."
                              onChange={(val) => {
                                setFieldValue("education_stage", val);
                                setFieldValue("extra_info", "");
                                setFieldValue("other_text", "");
                              }}
                            />
                          </div>

                          {/* Secondary Dropdown */}
                          {values.education_stage && (
  <div className="w-full">
    <FormikSelect
      name="extra_info"
      options={SECONDARY_OPTIONS[values.education_stage]}
      showLabel={true}
      label={`${PRIMARY_OPTIONS.find(option => option.value === values.education_stage)?.label || ""}, and`}
      id="extra_info"
      placeholder="Select.."
      onChange={(val) => {
        setFieldValue("extra_info", val);
        if (val !== "other") setFieldValue("other_text", "");
      }}
    />
  </div>
)}

                          {/* Other Text Field */}
                          {values.extra_info === "other" && (
                            <div className="w-full">
                              <FormikInput
                                name="other_text"
                                id="other_text"
                                label="Please specify"
                                type="input"
                                placeholder="Type here..."
                                maxLength={200}
                              />
                            </div>
                          )}

                          <div className="flex justify-start mt-5">
                            <Button
                              variant="primary"
                              type="submit"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? "Updating..." : "Update Info"}
                            </Button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </GlimpseCard>
          )}
        </>
      )}

      {userInfo.user.role === "user" && (
        <>
          <GlimpseCard
            title="Access"
            titleClass="text-h2"
            className="border-b rounded-none shadow-none border-border"
          >
            <p className="pt-1 pb-2 text-body-sm text-text-muted">
              Your membership includes 1 year access to CareerSight. Your account
              will not auto-renew, but you may choose to renew your membership
              at that time.
            </p>
            {userInfo.user.subscription.gift_by && (
              <div className="pb-2 text-body-lg-emphasis text-text-heading">Gifted by: {userInfo.user.subscription.gift_by}</div>
            )}
            <div className="pb-2 text-body-sm text-text-muted">
              Expires:&nbsp;
              {new Date(
                userInfo.user.subscription.expiry_date
              ).toLocaleDateString("en-US")}
            </div>
          </GlimpseCard>

          <GlimpseCard
            title="Subscribe to CareerSight email updates"
            titleClass="text-h2"
            className="rounded-none shadow-none"
          >
            <p className="pt-1 pb-3 text-body-sm text-text-muted">
              CareerSight occasionally sends updates to our valued users, like you. Stay up to date by subscribing to our emails.
            </p>
              <div>
                <Switch
                  checked={userInfo.user.profile.email_subscribed}
                  onChange={(checked) => {
                    handleSubscriptionChange(checked);
                    if (checked) {
                      displaySuccess("CareerSight email updates subscribed");
                    } else {
                      displaySuccess("CareerSight email updates unsubscribed");
                    }
                  }}
                />
              </div>
          </GlimpseCard>
        </>
      )}
    </div>
  );
};

export default SettingsScreen;